import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, NgIf } from '@angular/common';
import { NotificationService } from '../../../../Services/notification.service';
import { GstService } from '../../../../gst.service';
import { GstInvoiceService } from '../../../../Services/Invoice/gst-invoice.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OnlyNumberDirective } from '../../../../shared/only-number.directive';
import { AuthService } from '../../../../Services/auth.service';
import { StatesService } from '../../../../Services/states.service';
import { HttpParams } from '@angular/common/http';
import { UserCustomerService } from '../../../../Services/User/Customer/user-customer.service';
import { AccountService } from '../../../../Services/Accounts/account.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-invoice-gst-panel',
  standalone: true,
  imports: [FormsModule, CommonModule, OnlyNumberDirective, NgIf],
  templateUrl: './invoice-gst-panel.component.html',
  styleUrl: './invoice-gst-panel.component.css'
})
export class InvoiceGstPanelComponent implements OnInit {
  // New Fields for Direct Customer
  customerType: 'Retailer' | 'Direct' = 'Retailer';
  allStates: any[] = [];
  stateId: any = null;
  customerPan: string = ''; // Used for Direct Customer manual PAN

  customerName: string = '';
  address: string = '';
  gstin: string = '';
  mobile: string = '';
  silverRate: number | null = null;
  grandTotal: number | null = null;
  invoiceNo: string = '';
  paymentMode: string = '';
  paymentRef: string = '';
  placeOfSupply: string = '';
  filteredCustomers: any[] = [];
  finalMobile: string = '';
  finalName: string = '';
  customerDataFromGst: any;

  isFetchingGst: boolean = false;
  isSaving: boolean = false;

  // Multi-payment Fields
  cashAmount: number = 0;
  bankAmount: number = 0;
  onlineAmount: number = 0;
  cardAmount: number = 0;

  cashAccId: any = null;
  bankAccId: any = null;
  onlineAccId: any = null;
  cardAccId: any = null;

  cashAccounts: any[] = [];
  bankAccounts: any[] = [];
  onlineAccounts: any[] = [];
  cardAccounts: any[] = [];

  constructor(
    private notificationService: NotificationService,
    private gstService: GstService,
    private gstInvoiceService: GstInvoiceService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private statesService: StatesService,
    private userCustomerService: UserCustomerService,
    private accountService: AccountService,
  ) { }


  ngOnInit(): void {
    this.getNextInvoiceNumber();
    this.loadStates();
    this.loadAccounts();
    this.prefillCustomerFromRoute();
  }

  private prefillCustomerFromRoute(): void {
    const customerId = Number(this.route.snapshot.queryParamMap.get('customer_id'));
    const customerType = this.route.snapshot.queryParamMap.get('customer_type');

    // Prefill only when opened from GST customer card click
    if (!customerId) {
      return;
    }

    if (customerType === 'Retailer' || customerType === 'Direct') {
      this.customerType = customerType;
    }

    this.userCustomerService.getUserCustomerById(customerId).subscribe({
      next: (res: any) => {
        const customer = res?.data;
        if (!customer) return;

        if (customer.user_customer_type === 'Retailer') {
          this.customerType = 'Retailer';
        } else if (customer.user_customer_type) {
          this.customerType = 'Direct';
        }

        // Billing party name: firm/trade/billing name first, then person name
        const firmName = String(customer.billingname || customer.user_trade_name || '').trim();
        const personName = [customer.name, customer.user_last_name].filter(Boolean).join(' ').trim();
        this.customerName = firmName || personName;

        this.mobile = String(customer.mobilenumber || '').trim();
        this.finalMobile = this.mobile;
        this.gstin = String(customer.gstnumber || '').trim();
        this.customerPan = String(customer.user_pan_card_no || this.customerPan || '').trim();

        if (this.customerType === 'Direct') {
          this.gstin = '';
        }

        this.applyPrefillAddressFromCustomer(customer);

        // If master address is empty, fall back to latest GST bill for this mobile
        if (!this.normalizeAddress(this.address) && this.mobile) {
          this.prefillMissingDetailsFromLatestGstBill(this.mobile);
        }
      },
      error: () => {
        this.notificationService.showError('Unable to prefill selected customer details', 'Error');
      }
    });
  }

  private applyPrefillAddressFromCustomer(customer: any): void {
    const addressList = Array.isArray(customer?.address)
      ? customer.address
      : Array.isArray(customer?.addresses)
        ? customer.addresses
        : [];

    const addr = addressList[0];
    if (!addr || typeof addr !== 'object') {
      return;
    }

    this.address = this.normalizeAddress([
      addr.add1 || addr.add2,
      addr.city?.name,
      addr.state?.name,
      addr.pincode,
    ].filter(Boolean).join(', '));

    this.stateId = addr.state?.id ?? customer.state_id ?? this.stateId;
    if (addr.state?.name) {
      this.placeOfSupply = addr.state.name;
    }
  }

  private prefillMissingDetailsFromLatestGstBill(mobile: string): void {
    const params = new HttpParams().set('mobile', mobile);

    this.gstInvoiceService.getGstInvoices(params).subscribe({
      next: (res: any) => {
        const invoices = res?.data || [];
        if (!invoices.length) return;

        const latest = invoices[0];

        if (!this.customerName) {
          this.customerName = latest.customer_name || '';
        }

        if (!this.normalizeAddress(this.address)) {
          this.address = this.normalizeAddress(latest.customer_address);
        }

        if (!this.stateId && latest.state_id) {
          this.stateId = latest.state_id;
          this.placeOfSupply = latest.supply_place || this.placeOfSupply;
        }

        if (this.customerType === 'Retailer' && !this.gstin) {
          this.gstin = latest.customer_gstin || '';
        }

        if (this.customerType === 'Direct' && !this.customerPan) {
          this.customerPan = latest.customer_pan || '';
        }
      }
    });
  }

  loadAccounts() {
    const firmId = localStorage.getItem('firm_id');
    const params = firmId ? { firm_id: firmId } : {};

    this.accountService.getDropdowns(params).subscribe((res: any) => {
      if (res.success) {
        this.cashAccounts = res.data.cash_accounts || [];
        this.bankAccounts = res.data.bank_accounts || [];
        this.onlineAccounts = res.data.online_accounts || [];
        this.cardAccounts = res.data.card_accounts || [];

        // By default select the first account if it exists (Defaulting like NON-GST panel)
        if (this.cashAccounts.length > 0) this.cashAccId = this.cashAccounts[0].acc_id || this.cashAccounts[0].id;
        if (this.bankAccounts.length > 0) this.bankAccId = this.bankAccounts[0].acc_id || this.bankAccounts[0].id;
        if (this.onlineAccounts.length > 0) this.onlineAccId = this.onlineAccounts[0].acc_id || this.onlineAccounts[0].id;
        if (this.cardAccounts.length > 0) this.cardAccId = this.cardAccounts[0].acc_id || this.cardAccounts[0].id;

      }
    });
  }



  loadStates() {
    let params = new HttpParams();
    this.statesService.getStates(params).subscribe((res: any) => {
      if (res.success) {
        this.allStates = res.data.filter((s: any) => s.country.id === 85);
      }
    });
  }

  onStateChange() {
    const sid = Number(this.stateId);
    const selectedState = this.allStates.find(s => Number(s.id) === sid);
    if (selectedState) {
      this.placeOfSupply = selectedState.name;
    }
  }

  private truncate(value: number, decimals: number = 2): number {
    if (!value || isNaN(value)) return 0;
    const factor = Math.pow(10, decimals);
    return Math.floor(value * factor + 0.0000001) / factor;
  }

  getNextInvoiceNumber() {
    this.gstInvoiceService.getNextInvoiceNumber().subscribe((number: any) => {
      this.invoiceNo = number.data.invoice_no;
    });
  }

  validateForm(): boolean {
    if (!this.customerName || this.customerName.length < 3) {
      this.notificationService.showError('Enter a valid Customer Name (min 3 chars)', 'Validation');
      return false;
    }
    if (!this.mobile || !/^\d{10}$/.test(this.mobile)) {
      this.notificationService.showError('Enter a valid 10-digit Mobile Number', 'Validation');
      return false;
    }
    // Retailer specific validation
    if (this.customerType === 'Retailer' && (!this.gstin || this.gstin.length !== 15)) {
      this.notificationService.showError('GSTIN must be 15 characters long for Retailer', 'Validation');
      return false;
    }
    // Direct specific validation
    if (this.customerType === 'Direct' && !this.stateId) {
      this.notificationService.showError('Please select a State', 'Validation');
      return false;
    }
    // PAN Mandate for Direct > 2L
    if (this.customerType === 'Direct' && (this.grandTotal || 0) > 200000) {
      if (!this.customerPan || this.customerPan.length !== 10) {
        this.notificationService.showError('PAN Card is mandatory for transactions above ₹2,00,000', 'Validation');
        return false;
      }
    }
    if (!this.address || this.address.length < 5) {
      this.notificationService.showError('Please provide a detailed Address', 'Validation');
      return false;
    }
    if (this.totalPaid <= 0) {
      this.notificationService.showError('Please enter at least one payment amount (Cash/Bank/Online/Card)', 'Validation');
      return false;
    }

    if (!this.silverRate || this.silverRate <= 0) {
      this.notificationService.showError('Silver Rate must be greater than 0', 'Validation');
      return false;
    }
    if (!this.grandTotal || this.grandTotal <= 0) {
      this.notificationService.showError('Grand Total must be greater than 0', 'Validation');
      return false;
    }
    return true;
  }

  printInvoice() {
    if (this.isSaving) return;
    if (!this.validateForm()) return;

    this.isSaving = true;

    const invoicePayload = {
      firm_id: localStorage.getItem('firm_id'),
      customer_name: this.customerName,
      customer_type: this.customerType,
      customer_address: this.address,
      customer_gstin: this.customerType === 'Direct' ? '' : this.gstin,
      customer_pan: this.pan,
      customer_mobile: this.mobile,
      payment_mode: 'Multi', // Explicitly indicate multi-mode
      sub_total: this.total,
      cgst_amount: this.cgst,
      sgst_amount: this.sgst,
      igst_amount: this.igst,
      discount: this.discount,
      state_id: this.stateId,
      grand_total: this.grandTotal,
      quantity_per_gm: this.quantity,
      supply_place: this.placeOfSupply,
      payment_reference: this.paymentRef,
      rate_per_gm: this.silverRate,
      amount: this.amountX,
      // Payment Breakdown
      cash_amount: this.cashAmount,
      bank_amount: this.bankAmount,
      card_amount: this.cardAmount,
      online_amount: this.onlineAmount,
      // Payment Accounts
      cash_acc_id: this.cashAccId,
      bank_acc_id: this.bankAccId,
      card_acc_id: this.cardAccId,
      online_acc_id: this.onlineAccId
    };

    this.gstInvoiceService.createGstInvoice(invoicePayload)
      .pipe(finalize(() => { this.isSaving = false; }))
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.notificationService.showSuccess('Invoice saved successfully!', 'Success');
            this.router.navigate(['/invoice-gst', response.data.id]);
          }
        },
        (error) => this.notificationService.showError('Server error while saving invoice', 'Error')
      );
  }

  get currentDate(): string {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-GB', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day} - ${month} - ${year}`;
  }

  get pan(): string {
    if (this.customerType === 'Retailer') {
      return this.gstin && this.gstin.length >= 12 ? this.gstin.substring(2, 12).toUpperCase() : '';
    }
    return this.customerPan.toUpperCase();
  }

  get amountX(): number {
    return this.grandTotal ? this.truncate(this.grandTotal / 1.03, 2) : 0;
  }

  /**
   * Interstate logic preserved: true = Haryana (Local), false = Other (IGST)
   */
  get isInterstate(): boolean {
    if (this.customerType === 'Retailer') {
      return this.gstin ? String(this.gstin).startsWith('06') : true;
    } else {
      return Number(this.stateId) === 8; // Haryana ID 8
    }
  }

  get taxText1(): string { return this.isInterstate ? 'CGST @ 1.5%' : 'IGST @ 3%'; }
  get taxText2(): string { return this.isInterstate ? 'SGST @ 1.5%' : ''; }
  get cgst(): number { return this.isInterstate ? this.truncate(this.amountX * 0.015, 2) : 0; }
  get sgst(): number { return this.isInterstate ? this.truncate(this.amountX * 0.015, 2) : 0; }
  get igst(): number { return !this.isInterstate ? this.truncate(this.amountX * 0.03, 2) : 0; }

  get quantity(): string {
    if (this.silverRate && this.silverRate > 0) {
      const rawQty = this.amountX / this.silverRate;
      return this.truncate(rawQty, 4).toFixed(4);
    }
    return '0.0000';
  }

  get total(): number {
    const tax = this.isInterstate ? (this.cgst + this.sgst) : this.igst;
    return this.truncate(this.amountX + tax, 2);
  }

  get discount(): number {
    return this.grandTotal ? this.truncate(this.total - this.grandTotal, 2) : 0;
  }

  get adjustmentValue(): number {
    return this.discount > 0 ? -this.discount : Math.abs(this.discount);
  }

  get totalPaid(): number {

    return (this.cashAmount || 0) + (this.bankAmount || 0) + (this.onlineAmount || 0) + (this.cardAmount || 0);
  }

  get adjustmentClass(): string {
    return this.discount > 0 ? 'text-red-600' : 'text-green-600';
  }

  fetchGstDetails() {
    if (!this.gstin) {
      this.notificationService.showError('Enter a GST number first', 'Error');
      return;
    }
    this.isFetchingGst = true;
    this.gstService.getGstDetails(this.gstin).pipe(
      finalize(() => this.isFetchingGst = false)
    ).subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          const data = response.data;
          this.customerName = data.billingname || data.name || '';
          this.stateId = data.state || null;

          const addressData = data.address_data;
          const stateName = addressData?.state_name
            || this.allStates.find(s => Number(s.id) === Number(data.state))?.name
            || '';
          const cityName = addressData?.city_name || '';
          const lineAddress = data.address || '';
          const pincode = data.pincode || '';

          this.placeOfSupply = stateName;
          this.stateId = data.state || null;
          this.address = [lineAddress, cityName, stateName, pincode].filter(Boolean).join(', ');
          this.customerDataFromGst = data;

          this.resetAfterGstFetch();
          this.notificationService.showSuccess('Data fetched successfully', 'Success');
        } else {
          this.notificationService.showError(response?.message || 'GST details not found', 'Error');
        }
      },
      error: () => {
        this.notificationService.showError('Failed to fetch GST details. Please try again.', 'Error');
      }
    });
  }

  resetAfterGstFetch(): void {
    this.mobile = '';
    this.silverRate = null;
    this.grandTotal = null;
    this.paymentMode = '';
    this.paymentRef = '';
  }

  onMobileInput(event: any) {
    this.finalMobile = event.target.value;

    // 🔄 Reset auto-filled data when mobile is incomplete
    // if (this.finalMobile.length < 10) {
    //   this.resetCustomerFields();
    // }

    if (!this.finalMobile || this.finalMobile.length < 3) {
      this.filteredCustomers = [];
      return;
    }

    const params = new HttpParams().set('mobile', this.finalMobile);

    this.userCustomerService.getUserCustomers(params).subscribe((res: any) => {
      this.filteredCustomers = (res.data || []).slice(0, 5);

      // ✅ Auto-select ONLY when full mobile & exactly one match
      if (this.finalMobile.length === 10 && this.filteredCustomers.length === 1) {
        this.selectCustomer(this.filteredCustomers[0]);
        this.filteredCustomers = [];
      }
    });
  }

  onNameInput(event: any) {
    this.finalName = event.target.value;

    if (!this.finalName || this.finalName.length < 3) {
      this.filteredCustomers = [];
      return;
    }

    const params = new HttpParams().set('customer_name', this.finalName);

    this.userCustomerService.getUserCustomers(params).subscribe((res: any) => {
      // Show matches for firm/customer name; user selects from list (no auto-select on name length)
      this.filteredCustomers = (res.data || []).slice(0, 5);
    });
  }

  selectCustomer(customerData: any) {
    if (!customerData) return;

    const preserveGstAddress = this.hasGstFetchedAddress();
    const previousAddress = this.address;
    const previousStateId = this.stateId;
    const previousPlaceOfSupply = this.placeOfSupply;

    const params = new HttpParams().set('mobile', customerData.mobilenumber);

    this.gstInvoiceService.getGstInvoices(params).subscribe((res: any) => {
      const gstInvoices = (res.data || []);
      const latestInvoice = gstInvoices.length > 0 ? gstInvoices[0] : null;

      // Update Retailer / Direct from selected customer (or latest GST bill)
      this.customerType = this.resolveCustomerType(customerData, latestInvoice);

      if (latestInvoice) {
        if (this.customerType === 'Retailer') {
          this.gstin = latestInvoice.customer_gstin || customerData?.gstnumber || this.gstin || '';
        } else {
          this.gstin = '';
          this.customerPan = latestInvoice.customer_pan || customerData?.user_pan_card_no || this.customerPan;
        }

        this.customerName = this.resolveFirmName(customerData, latestInvoice.customer_name);
        this.mobile = latestInvoice.customer_mobile || customerData.mobilenumber || this.finalMobile;

        if (!preserveGstAddress) {
          this.stateId = latestInvoice.state_id ?? customerData?.state_id ?? customerData?.addresses?.[0]?.state?.id ?? this.stateId;
          this.address = this.normalizeAddress(latestInvoice.customer_address)
            || this.formatCustomerAddress(customerData)
            || previousAddress;
          if (latestInvoice.supply_place) {
            this.placeOfSupply = latestInvoice.supply_place;
          }
        }
      } else {
        if (this.customerType === 'Retailer') {
          this.gstin = customerData?.gstnumber || this.gstin || '';
        } else {
          this.gstin = '';
          this.customerPan = customerData?.user_pan_card_no || this.customerPan;
        }

        this.customerName = this.resolveFirmName(customerData);
        this.mobile = customerData.mobilenumber || this.finalMobile;

        if (!preserveGstAddress) {
          this.stateId = customerData?.addresses?.[0]?.state?.id
            ?? (Array.isArray(customerData?.address) ? customerData.address[0]?.state?.id : null)
            ?? customerData?.state_id
            ?? this.stateId;
          this.address = this.formatCustomerAddress(customerData) || previousAddress;
          const stateName = customerData?.addresses?.[0]?.state?.name
            || (Array.isArray(customerData?.address) ? customerData.address[0]?.state?.name : null);
          if (stateName) {
            this.placeOfSupply = stateName;
          }
        }
      }

      if (preserveGstAddress) {
        this.address = previousAddress;
        this.stateId = previousStateId;
        this.placeOfSupply = previousPlaceOfSupply;
      }

      this.filteredCustomers = [];
    });
  }

  private resolveCustomerType(customerData: any, invoice?: any): 'Retailer' | 'Direct' {
    const fromUser = String(customerData?.user_customer_type || '').trim().toLowerCase();
    if (fromUser === 'retailer') return 'Retailer';
    if (fromUser === 'direct customer' || fromUser === 'direct') return 'Direct';

    const fromInvoice = String(invoice?.customer_type || '').trim().toLowerCase();
    if (fromInvoice === 'retailer') return 'Retailer';
    if (fromInvoice === 'direct' || fromInvoice === 'direct customer') return 'Direct';

    if (customerData?.gstnumber || invoice?.customer_gstin) return 'Retailer';

    return this.customerType;
  }

  private resolveFirmName(customerData: any, fallbackName?: string): string {
    const firmName = String(
      customerData?.user_trade_name
      || customerData?.billingname
      || fallbackName
      || ''
    ).trim();

    const personName = [customerData?.name, customerData?.user_last_name].filter(Boolean).join(' ').trim();
    return firmName || personName || this.customerName;
  }

  getCustomerListAddress(customer: any): string {
    return this.formatCustomerAddress(customer)
      || this.normalizeAddress(customer?.customer_address)
      || '-';
  }

  private hasGstFetchedAddress(): boolean {
    return !!this.customerDataFromGst && !!this.normalizeAddress(this.address);
  }

  private normalizeAddress(value: unknown): string {
    if (value == null) return '';

    const text = String(value).trim();
    if (!text) return '';

    return text
      .split(',')
      .map(part => part.trim())
      .filter(part => part && part !== 'undefined')
      .join(', ');
  }

  private formatCustomerAddress(customerData: any): string {
    if (!customerData) return '';

    // Flat string address only (ignore address object arrays from user APIs)
    if (typeof customerData.address === 'string') {
      const flatAddress = this.normalizeAddress(customerData.address);
      if (flatAddress) return flatAddress;
    }

    const addr = customerData.addresses?.[0] || (Array.isArray(customerData.address) ? customerData.address[0] : null);
    if (!addr || typeof addr !== 'object') return '';

    return this.normalizeAddress([
      addr.add1 || addr.add2,
      addr.city?.name,
      addr.state?.name,
      addr.pincode,
    ].filter(Boolean).join(', '));
  }

  resetCustomerFields() {
    this.gstin = '';
    this.customerName = '';
    this.address = '';
    this.mobile = this.finalMobile;
  }

}